/**
 * Orchestra AI Platform - Test Panel Component
 * 
 * Panel for testing workflows with custom input data
 * 
 * @fileoverview Test panel with JSON input editor and result comparison
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useWorkflowStore } from '../../store/workflowStore';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

/**
 * Test Panel Component
 * @param {Object} props
 * @property {string} props.workflowId
 */
export function TestPanel({ workflowId }) {
  const { nodes, edges } = useWorkflowStore();
  const [inputData, setInputData] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  /**
   * Run test with current input
   */
  const runTest = async () => {
    setIsRunning(true);
    setTestResult(null);

    try {
      // Parse input data
      const data = JSON.parse(inputData);

      // Execute workflow via Edge Function
      const { data: result, error } = await supabase.functions.invoke('execute-workflow', {
        body: {
          workflow_id: workflowId,
          input_data: data,
          test_mode: true,
        },
      });

      if (error) throw error;

      setTestResult(result.data || result);
      toast.success('Test completed');
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
      });
      toast.error('Test failed: ' + error.message);
    } finally {
      setIsRunning(false);
    }
  };

  /**
   * Save test session
   */
  const saveTestSession = async () => {
    if (!testResult) {
      toast.error('No test result to save');
      return;
    }

    try {
      const { error } = await supabase.from('developer_test_sessions').insert({
        workflow_id: workflowId,
        test_data: JSON.parse(inputData),
        results: testResult,
        duration_ms: testResult.duration_ms || 0,
        status: testResult.success ? 'success' : 'error',
      });

      if (error) throw error;
      toast.success('Test session saved');
    } catch (error) {
      toast.error('Failed to save test session: ' + error.message);
    }
  };

  /**
   * Load previous test sessions
   */
  const loadTestSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('developer_test_sessions')
        .select('*')
        .eq('workflow_id', workflowId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    } catch (error) {
      toast.error('Failed to load test sessions: ' + error.message);
      return [];
    }
  };

  return (
    <div className="test-panel h-full flex flex-col bg-white border-t border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold mb-4">Test Workflow</h2>

        {/* Input Editor */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Input Data (JSON)
          </label>
          <textarea
            value={inputData}
            onChange={(e) => setInputData(e.target.value)}
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder='{"key": "value"}'
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={runTest}
            disabled={isRunning}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400"
          >
            {isRunning ? 'Running...' : 'Run Test'}
          </button>
          {testResult && (
            <button
              onClick={saveTestSession}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              Save Test Session
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4">
        {testResult ? (
          <div>
            <h3 className="text-sm font-semibold mb-2">Test Results</h3>
            <div
              className={`p-3 rounded mb-2 ${
                testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              Status: {testResult.success ? 'Success' : 'Failed'}
              {testResult.error && <div className="mt-1 text-sm">{testResult.error}</div>}
            </div>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No test results yet</p>
        )}
      </div>
    </div>
  );
}

TestPanel.propTypes = {
  workflowId: PropTypes.string.isRequired,
};

